import { type QueryClient } from "@tanstack/react-query"
import { toast } from "react-toastify"

import { noteColleagueChange } from "~/utils/editSession"
import { isDemo, isProduction } from "~/utils/environment"
import { type api } from "~/utils/api"
import {
  invalidateDemandReadsForEvent,
  refreshAfterPublish,
} from "~/utils/cacheUtils/demandCacheHelpers"
import {
  invalidateAllLineReaders,
  invalidateRateOfSaleReaders,
  refreshAllocationLines,
} from "~/utils/cacheUtils/allocationCacheHelpers"
import { refreshForecastDomainCache } from "~/utils/cacheUtils/forecastCacheHelpers"
import { refreshForecastedReturnsDomainCache } from "~/utils/cacheUtils/forecastedReturnsCacheHelpers"
import { type TableUpdateEvent, type ToastEvent, type UpdateEvent } from "~/utils/updates/events"
import { TAB_ID } from "~/utils/updates/tabId"

type Utils = ReturnType<typeof api.useUtils>

/**
 * Update handlers for the LISTEN/NOTIFY framework.
 *
 * INVARIANT: every invalidation triggered from this file MUST pass
 * `cancelRefetch: false`. Background bursts can produce many events in a
 * short window; cancelling in-flight refetches under that load is a
 * self-DDOS we have explicitly accepted as the trade-off.
 *
 * Edge case: a query that has been fetching for a long time will not be
 * re-issued by an invalidate (`cancelRefetch: false` skips the restart).
 * This is **agreed at a business level to be acceptable**. Do not add a
 * workaround. It is a decision we can consider changing in the future
 * but it is easier to enable than to disable,
 * so we start with the more conservative option.
 *
 * See `~/server/listenNotify` for the framework philosophy section.
 */

/**
 * Default `InvalidateOptions` (3rd arg of tRPC's invalidate) for this framework only.
 * Always pass these — see invariant note above.
 */
const baseInvalidateOptions = { cancelRefetch: false } as const

function dispatchTableUpdate(
  event: TableUpdateEvent,
  utils: Utils,
  _queryClient: QueryClient,
  currentUserId: string | null,
) {
  // Refresh the "Live" activity feed for every tableUpdate, including tables with no case below.
  utils.updates.getRecentActivity.invalidate(undefined, {}, baseInvalidateOptions)

  switch (event.table) {
    case "Task":
      utils.collection.tasksEventsForCollectionIds.invalidate(undefined, {}, baseInvalidateOptions)
      return
    case "ForecastedReturns": {
      utils.expectedReturns.getForecastedReturnsForVariants.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      utils.expectedReturns.getForecastedReturnsForCollections.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      utils.stockOutTimes.getStockOutTimesDetails.invalidate(undefined, {}, baseInvalidateOptions)
      utils.stockOutTimes.getClosingStockAndSuggestedOrders.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      utils.stockOutTimes.getSuggestedOrders.invalidate(undefined, {}, baseInvalidateOptions)
      utils.stockOutTimes.getSimulatedSuggestedOrders.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      utils.stockOutTimes.getSkuClosingStock.invalidate(undefined, {}, baseInvalidateOptions)
      utils.stockOutTimes.getStockLevelsForOrders.invalidate(undefined, {}, baseInvalidateOptions)
      utils.stockOutTimes.getProjectedStockForPeriods.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )

      // stockOutTimesV2 and getSummedForecastedReturns are NOT refreshed here —
      // dispatchUpdateBatch merges variantIds across the whole batch and refreshes each
      // once, scoped or broad, same rationale as ForecastVariant above.

      utils.incomingOrders.simulateAvailabilityImpact.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      return
    }
    case "Company":
      utils.company.getCompany.invalidate(undefined, {}, baseInvalidateOptions)
      return
    case "FeatureFlagOverride":
      utils.featureFlags.resolved.invalidate(undefined, {}, baseInvalidateOptions)
      utils.featureFlagsPanel.overview.invalidate(undefined, {}, baseInvalidateOptions)
      utils.featureFlagsPanel.history.invalidate(undefined, {}, baseInvalidateOptions)
      return
    case "VariantMetadata":
      // The originator's mutation already applied it optimistically and invalidates on settle.
      if (isOwnEvent(event, currentUserId)) return
      utils.variantMetadata.getReviewedVariantIds.invalidate(undefined, {}, baseInvalidateOptions)
      return
    case "ForecastVariant": {
      // refreshForecastDomainCache is NOT called here — dispatchUpdateBatch merges
      // variantIds across the whole batch and calls it once, so a replay/burst of many
      // ForecastVariant events triggers one scoped fetch instead of one per event.

      // Not covered by refreshForecastDomainCache — run for self too, else the editor's
      // own stock-out / out-of-sync views go stale.
      utils.stockOutTimes.getStockOutTimesDetails.invalidate(undefined, {}, baseInvalidateOptions)
      utils.stockOutTimes.getClosingStockAndSuggestedOrders.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      utils.stockOutTimes.getSuggestedOrders.invalidate(undefined, {}, baseInvalidateOptions)
      utils.stockOutTimes.getSkuClosingStock.invalidate(undefined, {}, baseInvalidateOptions)
      utils.stockOutTimes.getProjectedStockForPeriods.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      // These three read the same walk off the same forecast, so they go stale with it and
      // refreshForecastDomainCache covers none of them.
      utils.stockOutTimes.getSimulatedSuggestedOrders.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      utils.stockOutTimes.getStockLevelsForOrders.invalidate(undefined, {}, baseInvalidateOptions)
      utils.incomingOrders.simulateAvailabilityImpact.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      utils.purchasePlan.outOfSyncVariants.invalidate(undefined, {}, baseInvalidateOptions)
      return
    }
    case "UpdateMeta":
      // A suggested qty run started or ended; every client re-reads whether one is running.
      utils.replanishment.getSuggestedQuantityStatus.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      return
    case "AllocationRateOfSale":
      // The rate of sale was rebuilt in the background.
      invalidateRateOfSaleReaders(utils, baseInvalidateOptions)
      return
    case "CollectionOnSkuData":
      utils.collection.get.invalidate(undefined, {}, baseInvalidateOptions)
      utils.collection.getCollectionData.invalidate(undefined, {}, baseInvalidateOptions)
      utils.collection.getAllWithDetails.invalidate(undefined, {}, baseInvalidateOptions)
      utils.preOrder.getPreordersForCollectionByVariants.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      utils.sales.getSalesForCollectionPeriodOnVariant.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
      return
    default:
      // Unknown table — no-op. Adding a new publisher without a handler is
      // not an error; the client just doesn't react. Update this switch when
      // you want client-side invalidation for the new table.
      return
  }
}

function dispatchToast(event: ToastEvent) {
  switch (event.toastType) {
    case "success":
      toast.success(event.message)
      return
    case "error":
      toast.error(event.message)
      return
    case "info":
      toast.info(event.message)
      return
    case "warning":
      toast.warn(event.message)
      return
    default:
      toast(event.message)
      return
  }
}

function dispatchUpdate(
  event: UpdateEvent,
  utils: Utils,
  queryClient: QueryClient,
  currentUserId: string | null,
) {
  switch (event.eventType) {
    case "tableUpdate":
      dispatchTableUpdate(event, utils, queryClient, currentUserId)
      return
    case "toast":
      dispatchToast(event)
      return
    // Handled in UpdatesProvider before dispatch; this only guards a stray event reaching here.
    case "syncTooStale":
      return
    default: {
      const exhaustive: never = event
      const msg = `No update handler for eventType "${(exhaustive as { eventType?: string }).eventType ?? "unknown"}"`
      if (!isProduction() && !isDemo()) throw new Error(msg)
      return
    }
  }
}

/**
 * Whether this tab made the change and so already refreshed through its own mutation; the same
 * planner's other tabs did not. Events without a tab fall back to the user.
 */
export function isOwnEvent(event: UpdateEvent, currentUserId: string | null): boolean {
  if (event.tabId) return event.tabId === TAB_ID
  return event.userId != null && event.userId === currentUserId
}

function tableEvents(batch: UpdateEvent[], table: TableUpdateEvent["table"]): TableUpdateEvent[] {
  return batch.filter(
    (event): event is TableUpdateEvent =>
      event.eventType === "tableUpdate" && event.table === table,
  )
}

/** The events' ids as one list; `undefined` when any of them means the whole table. */
function mergeIds(events: TableUpdateEvent[], field: `${string}Ids`): string[] | undefined {
  const idLists = events.map((event) => event[field] as string[] | undefined)
  if (idLists.some((ids) => !ids || ids.length === 0)) return undefined
  return [...new Set(idLists.flatMap((ids) => ids!))]
}

/** The events' actions as one list; `undefined` when one of them doesn't say, so it means any. */
function mergeActions(events: TableUpdateEvent[]): string[] | undefined {
  if (events.some((event) => !event.actions)) return undefined
  return [...new Set(events.flatMap((event) => event.actions!))]
}

/**
 * Dispatches a whole batch (one live-coalesced burst, or one replay chunk) at once.
 * Per-event handling is unchanged, except for two tables whose handler runs a real
 * scoped fetch (not just an invalidate) — ForecastVariant's `refreshForecastDomainCache`
 * and ForecastedReturns' `refreshForecastedReturnsDomainCache`.
 * For those, variantIds are merged across the batch first, so a chunk carrying many events
 * of that table (a big replay, or a burst of concurrent edits) triggers one fetch instead
 * of one per event. See
 * `sync_replay_forecast_variant` / `sync_replay_forecasted_returns` (updates.ts) for how
 * often this actually fires in practice.
 */
export function dispatchUpdateBatch(
  batch: UpdateEvent[],
  utils: Utils,
  queryClient: QueryClient,
  currentUserId: string | null,
  onForeignForecastUpdate?: () => void,
) {
  for (const event of batch) dispatchUpdate(event, utils, queryClient, currentUserId)
  const foreign = (table: TableUpdateEvent["table"]) =>
    tableEvents(batch, table).filter((event) => !isOwnEvent(event, currentUserId))

  const forecastEdits = foreign("ForecastVariant")
  if (forecastEdits.length > 0) {
    onForeignForecastUpdate?.()
    void refreshForecastDomainCache(utils, mergeIds(forecastEdits, "variantIds"))
  }

  // No self-filter here — unlike ForecastVariant, the originator doesn't already know
  // the result locally (returns are computed by an async recompute), so self events
  // must refresh too.
  const returnsEvents = tableEvents(batch, "ForecastedReturns")
  if (returnsEvents.length > 0) {
    const returnsIds = mergeIds(returnsEvents, "variantIds")
    if (returnsIds) {
      void refreshForecastedReturnsDomainCache(utils, queryClient, returnsIds)
    } else {
      utils.productTableData.stockOutTimesV2.invalidate(undefined, {}, baseInvalidateOptions)
      utils.expectedReturns.getSummedForecastedReturns.invalidate(
        undefined,
        {},
        baseInvalidateOptions,
      )
    }
  }

  const demandEdits = foreign("DemandEditEvent")
  if (demandEdits.length > 0) {
    noteColleagueChange()
    void invalidateDemandReadsForEvent(
      utils,
      queryClient,
      mergeIds(demandEdits, "variantIds"),
      mergeActions(demandEdits),
    )
  }

  // A line settings save names its lines; a finished run, or a save too big to name them, doesn't.
  // The saving tab patched itself.
  const lineEvents = foreign("AllocationPlanLine")
  if (lineEvents.length > 0) {
    const lineIds = mergeIds(lineEvents, "lineIds")
    if (lineIds) void refreshAllocationLines(utils, queryClient, lineIds, baseInvalidateOptions)
    else invalidateAllLineReaders(utils, baseInvalidateOptions)
  }

  const demandPublishes = foreign("DemandPublish")
  if (demandPublishes.length > 0) {
    void refreshAfterPublish(utils, queryClient, mergeIds(demandPublishes, "variantIds"))
  }
}
